import { useId, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Callout } from '@/components/ui/callout'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multi-select'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import { requestErrorMessage } from '@/helpers/apiClient'
import { instantLabel } from '@/lib/instantLabel'
import { useGhlOptions, useRemoveGhlConnection, useUpdateGhlConnection } from '@/hooks/useGoHighLevel'
import {
  GHL_FIELD_SLOTS,
  GHL_FIELD_SLOT_LABEL,
  type GhlConnectionDto,
  type GhlConnectionOptionsDto,
  type GhlFieldMap,
  type GhlFieldSlot,
  type GhlStageRef,
} from '@lib/dto/gohighlevel.dto'
import {
  GHL_OPPORTUNITY_STATUSES,
  GHL_OPPORTUNITY_STATUS_LABEL,
  type GhlOpportunityStatus,
} from '@lib/enums/ghl-opportunity-status.enum'
import type { UpdateGhlConnectionInput } from '@lib/input/gohighlevel.input'
import type { VenueDto } from '@lib/dto/venue.dto'
import { GhlKeyPermissions } from './GhlKeyPermissions'
import { GhlFunnelMapEditor } from './GhlFunnelMapEditor'
import { possessive } from '@lib/helpers/format'

interface GhlConnectionCardProps {
  venue: VenueDto
  connection: GhlConnectionDto
}

const sameStage = (a: GhlStageRef, b: GhlStageRef): boolean => a.pipelineId === b.pipelineId && a.stageId === b.stageId

/** A stage as one string, for a picker that holds strings. GoHighLevel's ids never contain a newline. */
const stageKey = (ref: GhlStageRef): string => `${ref.pipelineId}\n${ref.stageId}`

function parseStageKey(key: string): GhlStageRef | null {
  const [pipelineId, stageId] = key.split('\n')
  return pipelineId !== undefined && stageId !== undefined ? { pipelineId, stageId } : null
}

/**
 * One connected sub-account: whether its key works, and what it feeds the event
 * picker — which stages and results, which contact fields hold the details copied
 * into an event, and the track its leads start on — and which of its stages mean
 * each step of the funnel.
 */
export function GhlConnectionCard({ venue, connection }: GhlConnectionCardProps) {
  const update = useUpdateGhlConnection(venue.id)
  const remove = useRemoveGhlConnection(venue.id)
  const working = connection.failingSince === null
  const options = useGhlOptions(venue.id, connection.id, working)
  const [error, setError] = useState<string | null>(null)
  const [confirmRemove, setConfirmRemove] = useState(false)
  // A working key is replaced only on purpose, so its field waits behind a link.
  // A refused key has nothing else to fix it, so its field is always open.
  const [replacing, setReplacing] = useState(false)
  const showReplace = replacing || !working

  function save(input: UpdateGhlConnectionInput, onSaved?: () => void): void {
    setError(null)
    update.mutate(
      { connectionId: connection.id, input },
      { onSuccess: () => onSaved?.(), onError: (e) => setError(requestErrorMessage(e)) },
    )
  }

  return (
    <Card asChild className="space-y-5 p-4">
      <section>
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="m-0 truncate text-sm font-medium text-ink">{connection.locationName}</p>
            <p className="m-0 truncate text-xs text-ink-muted">
              Key ending {connection.keyLast4}
              {connection.lastCheckedAt !== null ? ` · checked ${instantLabel(connection.lastCheckedAt)}` : ''}
              {showReplace ? null : (
                <>
                  {' · '}
                  <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => setReplacing(true)}>
                    Replace key
                  </Button>
                </>
              )}
            </p>
          </div>
          {working ? (
            <Badge tone="success" dot>
              Working
            </Badge>
          ) : (
            <Badge tone="danger" dot>
              Not working since {instantLabel(connection.failingSince ?? connection.createdAt)}
            </Badge>
          )}
        </header>

        {working ? null : (
          <Callout tone="danger" title="GoHighLevel stopped accepting this key">
            Make a new key in this sub-account (Settings → Private Integrations) and add it below to fix it.
          </Callout>
        )}

        {showReplace ? (
          <ReplaceKey
            busy={update.isPending}
            onReplace={(key) => save({ key }, () => setReplacing(false))}
            {...(working ? { onCancel: () => setReplacing(false) } : {})}
          />
        ) : null}

        {venue.tracks.length > 0 ? (
          <Field
            label="Track"
            htmlFor={`ghl-track-${connection.id}`}
            hint="Events created from this sub-account’s leads start on this track. Leads still appear for every track."
          >
            <Select
              id={`ghl-track-${connection.id}`}
              value={connection.trackId ?? ''}
              disabled={update.isPending}
              onChange={(e) => save({ trackId: e.target.value === '' ? null : e.target.value })}
            >
              <option value="">None</option>
              {venue.tracks.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}

        {working ? <ConnectionOptions connection={connection} options={options} busy={update.isPending} onSave={save} /> : null}

        {/* From the connection's last read, so it can be set up even while the key is refused.
            Keyed by what was saved, so a save starts the editor again from it. */}
        <GhlFunnelMapEditor
          key={JSON.stringify(connection.funnelMap)}
          venueId={venue.id}
          connection={connection}
          busy={update.isPending}
          onSave={save}
        />

        {error !== null ? <FormError>{error}</FormError> : null}

        <div className="border-t border-line pt-3">
          <Button variant="ghost" size="sm" onClick={() => setConfirmRemove(true)}>
            <Trash2 className="h-3.5 w-3.5" /> Remove connection
          </Button>
        </div>

        <ConfirmDialog
          open={confirmRemove}
          onOpenChange={setConfirmRemove}
          title="Remove this connection?"
          description={`${possessive(connection.locationName)} leads will stop appearing for this venue. Events already linked through it stay linked.`}
          confirmLabel="Remove"
          destructive
          busy={remove.isPending}
          error={remove.isError ? requestErrorMessage(remove.error) : null}
          onConfirm={() => remove.mutate(connection.id, { onSuccess: () => setConfirmRemove(false) })}
        />
      </section>
    </Card>
  )
}

interface ReplaceKeyProps {
  busy: boolean
  onReplace: (key: string) => void
  /** Closes the field. Absent while the key is refused, when a new one is the only fix. */
  onCancel?: () => void
}

/** Swap in a new key — GoHighLevel recommends rotating them. Everything else stays. */
function ReplaceKey({ busy, onReplace, onCancel }: ReplaceKeyProps) {
  const [key, setKey] = useState('')
  const id = useId()
  return (
    <div className="space-y-2">
      <Field
        label="Replace key"
        htmlFor={id}
        hint="Paste a new key from the same sub-account, with the permissions below. Its stages, fields and links stay."
      >
        {/* The button shares the input's row, so it lines up with the input rather than the hint. */}
        <div className="flex gap-2">
          <Input
            id={id}
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            autoComplete="off"
            size="sm"
            className="min-w-0 flex-1"
            // Opened by asking to replace the key, so the next thing is to paste one.
            autoFocus={onCancel !== undefined}
          />
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            disabled={busy || key.trim().length < 8}
            onClick={() => {
              onReplace(key.trim())
              setKey('')
            }}
          >
            Replace key
          </Button>
          {onCancel ? (
            <Button variant="ghost" size="sm" className="shrink-0" disabled={busy} onClick={onCancel}>
              Cancel
            </Button>
          ) : null}
        </div>
      </Field>
      <GhlKeyPermissions />
    </div>
  )
}

interface ConnectionOptionsProps {
  connection: GhlConnectionDto
  options: { data?: GhlConnectionOptionsDto | undefined; isPending: boolean; isError: boolean; error: Error | null }
  busy: boolean
  onSave: (input: UpdateGhlConnectionInput) => void
}

/** The stages, results and fields — read from GoHighLevel, so they are this account's own. */
function ConnectionOptions({ connection, options, busy, onSave }: ConnectionOptionsProps) {
  if (options.isPending) return <p className="text-sm text-ink-muted">Reading this sub-account from GoHighLevel…</p>
  if (options.isError || options.data === undefined) {
    return (
      <FormError>
        {options.error !== null ? requestErrorMessage(options.error) : 'GoHighLevel can’t be reached right now.'}
      </FormError>
    )
  }
  const data = options.data
  const ticked = connection.pickerStages
  const missing = data.missingStages.length > 0

  function toggleStage(ref: GhlStageRef, on: boolean): void {
    const kept = ticked.filter((s) => !sameStage(s, ref) && !data.missingStages.some((m) => sameStage(m, s)))
    onSave({ pickerStages: on ? [...kept, ref] : kept })
  }

  function toggleStatus(status: GhlOpportunityStatus, on: boolean): void {
    const next = on ? [...connection.pickerStatuses, status] : connection.pickerStatuses.filter((s) => s !== status)
    if (next.length > 0) onSave({ pickerStatuses: next })
  }

  function setField(slot: GhlFieldSlot, fieldId: string): void {
    const next: GhlFieldMap = { ...connection.fieldMap }
    if (fieldId === '') delete next[slot]
    else next[slot] = fieldId
    onSave({ fieldMap: next })
  }

  const stageOptions: MultiSelectOption[] = data.pipelines.flatMap((pipeline) =>
    pipeline.stages.map((stage) => ({
      value: stageKey({ pipelineId: pipeline.id, stageId: stage.id }),
      label: stage.name,
      group: pipeline.name,
    })),
  )
  const statusOptions: MultiSelectOption[] = GHL_OPPORTUNITY_STATUSES.map((status) => {
    const last = connection.pickerStatuses.length === 1 && connection.pickerStatuses[0] === status
    return {
      value: status,
      label: GHL_OPPORTUNITY_STATUS_LABEL[status],
      ...(last ? { disabled: true, disabledReason: 'Keep at least one' } : {}),
    }
  })

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <p className="m-0 text-sm font-medium text-ink">Leads shown when creating an event</p>
        {missing ? (
          <Callout tone="attention">
            Some stages chosen here no longer exist in GoHighLevel. Choose the stages again.
          </Callout>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Stages"
            htmlFor={`ghl-stages-${connection.id}`}
            hint="The stages a couple is in when they may be booking. Only leads in these stages are offered."
          >
            <MultiSelect
              id={`ghl-stages-${connection.id}`}
              options={stageOptions}
              selected={ticked.map(stageKey)}
              disabled={busy}
              placeholder="None yet, so no leads are offered"
              onToggle={(value) => {
                const ref = parseStageKey(value)
                if (ref !== null) toggleStage(ref, !ticked.some((s) => sameStage(s, ref)))
              }}
            />
          </Field>
          <Field
            label="Results"
            htmlFor={`ghl-statuses-${connection.id}`}
            hint="GoHighLevel’s own result for each lead. Couples who booked are often still open or even lost there."
          >
            <MultiSelect
              id={`ghl-statuses-${connection.id}`}
              options={statusOptions}
              selected={connection.pickerStatuses}
              disabled={busy}
              summary={(chosen) =>
                chosen.length === GHL_OPPORTUNITY_STATUSES.length ? 'All results' : chosen.map((o) => o.label).join(', ')
              }
              onToggle={(value) => {
                const status = GHL_OPPORTUNITY_STATUSES.find((s) => s === value)
                if (status !== undefined) toggleStatus(status, !connection.pickerStatuses.includes(status))
              }}
            />
          </Field>
        </div>
      </div>

      <fieldset className="space-y-3">
        <legend className="text-sm font-medium text-ink">Details copied into the event</legend>
        <p className="m-0 text-xs text-ink-muted">
          Which of this sub-account’s contact fields holds each detail. Suggested ones are marked; change any that are wrong.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {GHL_FIELD_SLOTS.map((slot) => {
            const value = connection.fieldMap[slot] ?? ''
            const suggested = value !== '' && data.suggestedFieldMap[slot] === value
            const id = `ghl-field-${connection.id}-${slot}`
            return (
              <Field
                key={slot}
                htmlFor={id}
                size="sm"
                label={
                  <span className="inline-flex items-center gap-2">
                    {GHL_FIELD_SLOT_LABEL[slot]}
                    {suggested ? <Badge tone="info">Suggested</Badge> : null}
                  </span>
                }
              >
                <Select id={id} size="sm" value={value} disabled={busy} onChange={(e) => setField(slot, e.target.value)}>
                  <option value="">Not copied</option>
                  {data.contactFields.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name} — {f.fieldKey}
                    </option>
                  ))}
                </Select>
              </Field>
            )
          })}
        </div>
      </fieldset>
    </div>
  )
}
